export interface GpxTrackPoint {
  lat: number;
  lon: number;
  timestamp: number;
  speed?: number;
}

export interface GpxTrace {
  name: string;
  points: GpxTrackPoint[];
}

export class GpxParser {
  static parse(gpxContent: string): GpxTrace {
    const parser = new DOMParser();
    const doc = parser.parseFromString(gpxContent, 'application/xml');
    
    const trackName = doc.querySelector('trk name')?.textContent || 'Trace GPX';
    const trackPoints = Array.from(doc.querySelectorAll('trkpt'));
    
    const points: GpxTrackPoint[] = [];
    
    for (let i = 0; i < trackPoints.length; i++) {
      const trkpt = trackPoints[i];
      const lat = parseFloat(trkpt.getAttribute('lat') || '0');
      const lon = parseFloat(trkpt.getAttribute('lon') || '0');
      
      const timeElement = trkpt.querySelector('time');
      let timestamp = Date.now() + i * 1000; // Fallback: 1 point par seconde
      
      if (timeElement?.textContent) {
        timestamp = new Date(timeElement.textContent).getTime();
      }
      
      // D'abord essayer de lire la vitesse depuis le GPX
      let speed: number | undefined;
      const speedElement = trkpt.querySelector('speed');
      if (speedElement?.textContent) {
        // La vitesse dans le GPX est en m/s
        speed = parseFloat(speedElement.textContent);
      } else if (i > 0) {
        // Sinon calculer la vitesse par rapport au point précédent
        const prevPoint = points[i - 1];
        const distance = GpxParser.haversineDistance(
          prevPoint.lat, prevPoint.lon, lat, lon
        );
        const timeDiff = (timestamp - prevPoint.timestamp) / 1000; // en secondes
        if (timeDiff > 0) {
          speed = distance / timeDiff; // m/s
        }
      }
      
      points.push({ lat, lon, timestamp, speed });
    }
    
    return { name: trackName, points };
  }
  
  private static haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371000; // Rayon de la Terre en mètres
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }
  
  static skipToOffset(trace: GpxTrace, offsetMinutes: number): GpxTrace {
    if (trace.points.length === 0) return trace;
    
    const startTime = trace.points[0].timestamp;
    const offsetMs = offsetMinutes * 60 * 1000;
    const targetTime = startTime + offsetMs;
    
    // Trouver le premier point après l'offset
    const startIndex = trace.points.findIndex(point => point.timestamp >= targetTime);
    
    if (startIndex === -1) {
      console.warn(`Offset de ${offsetMinutes}min dépasse la durée de la trace`);
      return trace;
    }
    
    return {
      ...trace,
      points: trace.points.slice(startIndex)
    };
  }

  static skipToFraction(trace: GpxTrace, fraction: number): GpxTrace {
    if (trace.points.length === 0) return trace;
    
    const startIndex = Math.floor(trace.points.length * fraction);
    
    return {
      ...trace,
      points: trace.points.slice(startIndex)
    };
  }

  static filterStationarySegments(trace: GpxTrace): GpxTrace {
    if (trace.points.length === 0) return trace;
    
    const filteredPoints: GpxTrackPoint[] = [];
    let stationaryCount = 0;
    const maxConsecutiveStationary = 10; // Garder max 10 points stationnaires consécutifs
    
    for (let i = 0; i < trace.points.length; i++) {
      const point = trace.points[i];
      const speed = point.speed || 0;
      const isStationary = speed < 0.5; // < 1.8 km/h considéré comme stationnaire
      
      if (isStationary) {
        stationaryCount++;
        // Garder seulement les premiers et derniers points stationnaires d'une séquence
        if (stationaryCount <= 3 || i === trace.points.length - 1) {
          filteredPoints.push(point);
        } else if (stationaryCount === maxConsecutiveStationary) {
          // Ajouter un point intermédiaire pour marquer la pause
          filteredPoints.push(point);
        }
      } else {
        // Point en mouvement
        stationaryCount = 0;
        filteredPoints.push(point);
      }
    }
    
    console.log(`Filtrage: ${trace.points.length} points → ${filteredPoints.length} points (${Math.round(100 * filteredPoints.length / trace.points.length)}%)`);
    
    return {
      ...trace,
      points: filteredPoints
    };
  }
}